import { formatCompactNumber, formatNumberWithCommas } from "./format-number";
import Decimal from "decimal.js";

export interface FormatUsdOptions {
  /** When true, render compact (`"$1.2M"`). Default false. */
  compact?: boolean;
  /** Override decimal places. Defaults: 2 (full), 1 (compact). */
  decimalPlaces?: number;
  /** Trim trailing zeros. Default true for compact, false for full. */
  trimTrailingZeros?: boolean;
}

function toNumber(value: number | string | Decimal): number {
  if (typeof value === "number") return value;
  if (value instanceof Decimal) return value.toNumber();
  return new Decimal(value).toNumber();
}

/**
 * Format a USD value as a plain `"$"`-prefixed string. Pure — no `Intl`, no
 * `toLocale*`. Always renders as `$` (never `US$`) regardless of browser
 * locale.
 *
 * Accepts `number`, decimal string, or `Decimal` instance — the helper
 * converts to `number` internally. Acceptable because display values are
 * well under `Number.MAX_SAFE_INTEGER` (the `Decimal.js ONLY` rule in
 * `CLAUDE.md` is about arithmetic precision, not display).
 *
 * @example
 * formatUsd(1234.56)                              // "$1,234.56"
 * formatUsd(1234, { decimalPlaces: 0 })           // "$1,234"
 * formatUsd(1234567, { compact: true })           // "$1.2M"
 * formatUsd(1000, { compact: true })              // "$1K"
 * formatUsd("123456789.12", { decimalPlaces: 2 }) // "$123,456,789.12"
 */
export function formatUsd(value: number | string | Decimal, options?: FormatUsdOptions): string {
  const num = toNumber(value);
  if (!Number.isFinite(num)) return "$0";

  const compact = options?.compact ?? false;
  const decimals = options?.decimalPlaces ?? (compact ? 1 : 2);
  const trim = options?.trimTrailingZeros ?? compact;

  const sign = num < 0 ? "-" : "";
  const abs = Math.abs(num);
  const body = compact
    ? formatCompactNumber(abs, { decimals, trimTrailingZeros: trim })
    : formatNumberWithCommas(abs, { decimals, trimTrailingZeros: trim });

  return `${sign}$${body}`;
}
